/*
 * enquiry-tracking.js
 *
 * Product enquiry -> thank-you page conversion tracking.
 *
 *   config : snippets/enquiry-tracking.liquid       (window.acjEnquiry)
 *   guard  : snippets/enquiry-redirect-guard.liquid (pre-paint redirect)
 *   markup : snippets/modal-enquire.liquid          (form id="EnquiryForm")
 *
 * This file is the single owner of the acj_enquiry storage key. It writes the submit
 * payload, sweeps stale payloads, reopens the modal after a server-side error, and pushes
 * both dataLayer events. Nothing else may touch that key.
 *
 * Events:
 *   enquiry_submit - product page, on submit. Funnel diagnostics, NOT the conversion.
 *   generate_lead  - thank-you page, on load. THE conversion. Fires at most once.
 *
 * No PII in any payload. Name, email, phone and the message body go to Shopify's contact
 * email and nowhere else.
 */
(function () {
  var CFG = window.acjEnquiry;
  if (!CFG) return;

  window.dataLayer = window.dataLayer || [];

  function push(payload) {
    /* Clear the previous ecommerce object so stale items never leak into the next event. */
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  }

  function newEnquiryId() {
    return 'enq_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  }

  function num(v) {
    var n = parseFloat(v);
    return isFinite(n) ? n : 0;
  }

  function buildItems(p, value) {
    p = p || {};
    return [
      {
        item_id: String(p.variant_id || p.product_id || ''),
        item_name: p.product_title || '',
        item_brand: p.product_vendor || '',
        item_category: p.product_type || '',
        item_variant: p.variant_title || '',
        price: value,
        quantity: 1
      }
    ];
  }

  function readPayload() {
    try {
      var raw = sessionStorage.getItem(CFG.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function clearPayload() {
    try {
      sessionStorage.removeItem(CFG.storageKey);
    } catch (e) {
      /* storage unavailable */
    }
  }

  /* ------------------------------------------------------------------ */
  /* Product page                                                        */
  /* ------------------------------------------------------------------ */

  function initProduct() {
    var product = {};
    var src = CFG.product || {};
    for (var k in src) {
      if (Object.prototype.hasOwnProperty.call(src, k)) product[k] = src[k];
    }

    var form = document.getElementById(CFG.formId);
    var modal = document.querySelector(CFG.modalSelector);
    var opener = document.getElementById(CFG.openerId);

    /* Read the flag BEFORE the sweep clears it — the reopen decision depends on it. */
    var stored = readPayload();
    var wasOurs = !!stored;

    /* Staleness sweep. If a payload survived a page load without the redirect guard
       consuming it, the previous submission failed server-side. Its only legitimate
       lifetime is submit -> next page load, so anything still here is stale. This is what
       stops a later back-in-stock success from being misattributed as an enquiry. */
    clearPayload();

    /* Reopen the modal when the page came back from a failed POST of OUR form.
       form.errors is true inside every {% form 'contact' %} on the page, so the Liquid
       marker alone cannot identify the submitter — hence the storage flag, with the URL
       fragment as the no-storage fallback. */
    if (form && modal) {
      var marker = form.querySelector('[data-enquiry-state]');
      var state = marker ? marker.getAttribute('data-enquiry-state') : '';
      var hashIsOurs = window.location.hash === '#' + CFG.formId;

      if (state && (wasOurs || hashIsOurs)) {
        customElements.whenDefined('modal-dialog').then(function () {
          if (typeof modal.show !== 'function') return;
          modal.show(opener);
          /* Just past ModalDialog.show()'s own 300ms trapFocus timer. autofocus does not
             work here — the element was visibility:hidden at parse time. */
          window.setTimeout(function () {
            var status = document.getElementById(CFG.formId + '-status');
            if (status) status.focus();
          }, 350);
        });
      }
    }

    /* Keep the pre-filled product context current with the variant picker.
       renderProductInfo() in theme.js only swaps #price/#Sku/#Inventory/#Volume — nothing
       updates this modal for us. */
    var fProduct = form && form.querySelector('[data-enquiry-product]');
    var fUrl = form && form.querySelector('[data-enquiry-product-url]');
    var fSku = form && form.querySelector('[data-enquiry-sku]');
    var fVariantLabel = modal && modal.querySelector('[data-enquiry-variant-title]');

    function applyVariant(v) {
      if (!v) return;
      product.variant_id = String(v.id);
      if (v.title) product.variant_title = v.title;
      product.sku = v.sku || '';
      if (typeof v.price === 'number') product.price = v.price / 100;

      var label = product.product_title + ' — ' + (product.variant_title || '');
      if (fProduct) fProduct.value = label;
      if (fUrl) fUrl.value = String(fUrl.value).split('?')[0] + '?variant=' + product.variant_id;
      if (fSku) fSku.value = product.sku;
      if (fVariantLabel) fVariantLabel.textContent = ' — ' + (product.variant_title || '');
    }

    /* PUB_SUB_EVENTS is a top-level `const` in assets/constants.js, so it is a global
       lexical binding and NOT a property of window — it must be reached by bare name.
       theme.js publishes { data: { sectionId, html, variant } }. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        applyVariant(event && event.data && event.data.variant);
      });
    }

    /* Fallback when the pubsub event does not fire: buy-buttons.liquid renders the live
       variant id with class="product-variant-id". */
    function currentVariantId() {
      var el = document.querySelector('.product-variant-id');
      return el && el.value ? String(el.value) : product.variant_id;
    }

    /* Delegated on document, capture phase: the form lives inside a <modal-dialog>, so this
       avoids timing assumptions and survives any later stopPropagation(). The submit event
       does not fire when HTML constraint validation fails, so a browser-blocked bad email
       never writes a flag. */
    document.addEventListener(
      'submit',
      function (e) {
        var target = e.target;
        if (!target || target.id !== CFG.formId) return;

        product.variant_id = currentVariantId();

        if (fSku && !fSku.value) fSku.removeAttribute('name');

        var value = num(product.price) || num(CFG.defaultLeadValue);
        var enquiryId = newEnquiryId();
        var enquiry = {};
        for (var key in product) {
          if (Object.prototype.hasOwnProperty.call(product, key)) enquiry[key] = product[key];
        }
        enquiry.form_id = CFG.formId;
        enquiry.form_location = 'product_page';

        /* Written synchronously, before the browser begins navigating. */
        try {
          sessionStorage.setItem(
            CFG.storageKey,
            JSON.stringify({
              ts: Date.now(),
              enquiry_id: enquiryId,
              value: value,
              currency: product.currency || CFG.currency,
              enquiry: enquiry
            })
          );
        } catch (err) {
          /* storage unavailable — the URL fallback on the thank-you page covers us */
        }

        push({
          event: 'enquiry_submit',
          enquiry_id: enquiryId,
          enquiry: enquiry,
          ecommerce: {
            currency: product.currency || CFG.currency,
            value: value,
            items: buildItems(product, value)
          }
        });
      },
      true
    );
  }

  /* ------------------------------------------------------------------ */
  /* Thank-you page                                                      */
  /* ------------------------------------------------------------------ */

  function initThankYou() {
    var params = new URLSearchParams(window.location.search);
    var payload = readPayload();
    var source = 'session';

    /* Consume: one shot only. Reloads, Back/Forward and bookmarks must not re-fire. */
    if (payload) clearPayload();
    if (payload && payload.ts && Date.now() - payload.ts > CFG.ttlMs) payload = null;

    /* Reduced fallback when browser storage was unavailable. */
    if (!payload && params.get('enq') === '1') {
      source = 'url';
      payload = {
        enquiry_id: params.get('eid') || newEnquiryId(),
        value: num(params.get('val')),
        currency: params.get('cur') || CFG.currency,
        enquiry: {
          form_id: CFG.formId,
          form_location: 'product_page',
          product_id: params.get('pid') || '',
          variant_id: params.get('vid') || '',
          currency: params.get('cur') || CFG.currency,
          price: num(params.get('val'))
        }
      };
    }

    /* Direct hit, bookmark or organic landing: fire nothing. */
    if (!payload) return;

    var value = num(payload.value) || num(CFG.defaultLeadValue);

    push({
      event: 'generate_lead',
      enquiry_id: payload.enquiry_id,
      lead_type: 'product_enquiry',
      lead_source: 'product_page_enquiry_form',
      payload_source: source,
      currency: payload.currency || CFG.currency,
      value: value,
      estimated_lead_value: Math.round(value * CFG.leadValueRatio * 100) / 100,
      enquiry: payload.enquiry,
      ecommerce: {
        currency: payload.currency || CFG.currency,
        value: value,
        items: buildItems(payload.enquiry, value)
      }
    });
  }

  if (CFG.context === 'product') initProduct();
  else if (CFG.context === 'thank_you') initThankYou();
})();
